import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileTailwind from "./MobileTailwind";
import HomeHeader from "./components/HomeHeader";
import BottomNav from "./components/BottomNav";
import OrderCard from "./components/OrderCard";
import { getEncryptedSessionData, setEncryptedSessionData } from "../Services/SessionStorageService";
import { getPosOrderCacheKey, posSocket, normalizeTableNo } from "./Tables";
import SubHeader from "./components/SubHeader";
import qrOrderRealtimeService, { QrOrderPayload } from "../Services/QrOrderRealtimeService";

const Orders = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const tenantId = localStorage.getItem("tenantId") || "default";

  // Cache se real occupied tables lo
  const [posOrders, setPosOrders] = useState<Record<string, any>>(() => {
    return getEncryptedSessionData<Record<string, any>>(getPosOrderCacheKey(tenantId)) || {};
  });

  // 1. QrOrderRealtimeService subscriber (Backend Socket / REST Polling)
  useEffect(() => {
    const unsubscribe = qrOrderRealtimeService.subscribe((order: QrOrderPayload) => {
      // ── Tenant ID check ──
      const orderTenantId = String(order.tenantId || order.tenant_id || "");
      const localTenantId = localStorage.getItem("tenantId") || "default";
      if (orderTenantId && orderTenantId !== localTenantId) {
        console.log("Tenant ID mismatch, ignoring order event:", orderTenantId, "expected:", localTenantId);
        return;
      }

      const tableNo = String(order.tableNo || order.table_no || "").trim();
      if (!tableNo) return;
      const action = order.action || "ADD";
      const incomingType = order.type || "";

      if (action === "CLEAR_TABLE") {
        setPosOrders((prev) => {
          const next = { ...prev };
          delete next[tableNo];
          setEncryptedSessionData(getPosOrderCacheKey(tenantId), next);
          return next;
        });
        return;
      }

      if (incomingType === "POS_ORDER") {
        const freshMenus = order.menus || [];
        setPosOrders((prev) => {
          const next = {
            ...prev,
            [tableNo]: {
              menus: freshMenus,
              timestamp: order.timestamp || new Date().toISOString(),
              type: "POS_ORDER",
              loungeId: order.loungeId || order.lounge_id,
              tokenNumber: order.tokenNumber || order.token_number,
            }
          };
          setEncryptedSessionData(getPosOrderCacheKey(tenantId), next);
          return next;
        });
        return;
      }

      if (incomingType === "QR_ORDER") {
        setPosOrders((prev) => {
          const next = {
            ...prev,
            [tableNo]: {
              menus: order.menus || [],
              timestamp: order.timestamp || new Date().toISOString(),
              type: "QR_ORDER",
              loungeId: order.loungeId || order.lounge_id,
              tokenNumber: order.tokenNumber || order.token_number,
            }
          };
          setEncryptedSessionData(getPosOrderCacheKey(tenantId), next);
          return next;
        });
        return;
      }
    });

    return unsubscribe;
  }, [tenantId]);

  // 2. POS Socket direct listener (Local area updates)
  useEffect(() => {
    const s = posSocket();

    const handleWaiterOrder = (payload: any) => {
      // Tenant ID validation
      const eventTenantId = String(payload?.tenantId || payload?.tenant_id || "");
      const localTenantId = localStorage.getItem("tenantId") || "default";
      if (eventTenantId && eventTenantId !== localTenantId) {
        return;
      }

      const tableNo = normalizeTableNo(payload?.table_no || payload?.tableNo);
      if (!tableNo) return;
      const action = payload?.action || "";

      if (action === "CLEAR_TABLE") {
        setPosOrders((prev) => {
          const next = { ...prev };
          delete next[tableNo];
          setEncryptedSessionData(getPosOrderCacheKey(tenantId), next);
          return next;
        });
        return;
      }

      // Fetch or update menus
      const envUrl = process.env.REACT_APP_POS_SOCKET_URL;
      const stored = localStorage.getItem("pos_socket_url");
      const host = window.location.hostname;
      let posUrl = envUrl || stored || "http://localhost:3007";
      if (!envUrl && (!stored || stored === "http://localhost:3007") && host !== "localhost" && host !== "127.0.0.1") {
        posUrl = `http://${host}:3007`;
      }

      fetch(`${posUrl}/get-order-by-table/${tableNo}`)
        .then((r) => r.json())
        .then((res: any) => {
          const menus: any[] = res?.menus || [];

          setPosOrders((prev) => {
            const next = {
              ...prev,
              [tableNo]: {
                menus,
                timestamp: res?.timestamp || payload?.timestamp || prev[tableNo]?.timestamp || new Date().toISOString(),
                type: "POS_ORDER",
                loungeId: payload?.lounge_id || prev[tableNo]?.loungeId,
                tokenNumber: payload?.token_number || res?.token_number || prev[tableNo]?.tokenNumber || "",
              }
            };
            setEncryptedSessionData(getPosOrderCacheKey(tenantId), next);
            return next;
          });
        })
        .catch(() => {
          // Fallback: Just mark as occupied if fetch fails
          setPosOrders((prev) => {
            const next = {
              ...prev,
              [tableNo]: {
                menus: prev[tableNo]?.menus || [{ id: "ph", name: "Order placed", quantity: 1, gross_price: 0 }],
                timestamp: prev[tableNo]?.timestamp || new Date().toISOString(),
                type: "POS_ORDER",
                loungeId: payload?.lounge_id || prev[tableNo]?.loungeId,
                tokenNumber: payload?.token_number || prev[tableNo]?.tokenNumber || "",
              }
            };
            setEncryptedSessionData(getPosOrderCacheKey(tenantId), next);
            return next;
          });
        });
    };

    const handleBillCleared = (payload: any) => {
      // Tenant ID validation
      const eventTenantId = String(payload?.tenantId || payload?.tenant_id || "");
      const localTenantId = localStorage.getItem("tenantId") || "default";
      if (eventTenantId && eventTenantId !== localTenantId) {
        return;
      }

      const tableNo = normalizeTableNo(payload?.table_no || payload?.tableNo);
      if (!tableNo) return;

      setPosOrders((prev) => {
        const next = { ...prev };
        delete next[tableNo];
        setEncryptedSessionData(getPosOrderCacheKey(tenantId), next);
        return next;
      });
    };

    s.on("waiterOrderCreated", handleWaiterOrder);
    s.on("orderBillCleared", handleBillCleared);
    s.on("tableBillCleared", handleBillCleared);
    s.on("CLEAR_TABLE", handleBillCleared);

    if (!s.connected) s.connect();

    return () => {
      s.off("waiterOrderCreated", handleWaiterOrder);
      s.off("orderBillCleared", handleBillCleared);
      s.off("tableBillCleared", handleBillCleared);
      s.off("CLEAR_TABLE", handleBillCleared);
    };
  }, [tenantId]);

  const occupiedTables = Object.keys(posOrders).filter(
    (tableNo) => posOrders[tableNo]?.menus?.length > 0
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return occupiedTables;
    const q = search.toLowerCase();
    return occupiedTables.filter((t) => t.toLowerCase().includes(q));
  }, [search, occupiedTables.join(",")]);

  return (
    <>
      <MobileTailwind />
      <div id="mobile-root" className="h-screen w-full bg-black flex justify-center overflow-hidden">
        <div className="relative w-full h-screen overflow-y-auto overflow-x-hidden">
          {/* Background image + overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="/mobile-assets/login-bg.jpg"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/80" />
          </div>

          <div className="relative z-10">
            <SubHeader title="Ongoing Orders" showBack={false} />
            <HomeHeader
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search Table Number"
            />

            <div className="px-3 md:px-6 pt-3 pb-[120px]">
              <h2 className="font-sansation font-bold text-white text-[16px] mb-3">
                Ongoing Orders
              </h2>

              {filtered.length === 0 ? (
                <p className="text-center font-opensans text-[14px] text-[#F4F4F5] mt-6">
                  No ongoing orders
                </p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                  {filtered.map((t) => (
                    <OrderCard
                      key={t}
                      tableNo={t}
                      onClick={() => navigate(`/mobile/order-details/${t}`)}
                    />
                  ))}
                </div>
              )}
            </div>

            <BottomNav active="orders" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Orders;